import { TopBar, type TopBarBreadcrumb } from '@zyxantech/tux'
import { Bell } from 'lucide-react'
import { useLocation, useNavigate } from 'react-router-dom'
import { ROUTES } from '@/constants/routes'
import { useAlarmCount } from '@/hooks/useAlarmCount'
import { useManifest } from '@/hooks/useManifest'
import { useTenant } from '@/hooks/useTenant'
import { useUiStore } from '@/store/ui.store'
import { TapBrand } from './TapBrand'

/** Humanise a path segment as a breadcrumb fallback (e.g. "asset-detail"). */
function humanise(segment: string): string {
  return segment.replace(/[-_]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

/**
 * Top bar: breadcrumb derived from the active route + manifest labels, a
 * hamburger that toggles the sidebar, and quick actions (alarms bell).
 */
export function AppTopBar() {
  const { navSections } = useManifest()
  const { tenantName } = useTenant()
  const toggleSidebar = useUiStore((s) => s.toggleSidebar)
  const alarmCount = useAlarmCount()
  const navigate = useNavigate()
  const location = useLocation()

  const navItems = navSections.flatMap((s) => s.items)
  const current = navItems.find(
    (item) => location.pathname === item.route || location.pathname.startsWith(`${item.route}/`),
  )

  // The TAP wordmark now lives in the brand (leading slot); the breadcrumb leads
  // with the tenant, mirroring the demo's "Precision Auto › <page>".
  const breadcrumb: TopBarBreadcrumb[] = []
  if (tenantName) breadcrumb.push({ label: tenantName, href: ROUTES.OVERVIEW })
  if (current) {
    breadcrumb.push({ label: current.label, href: current.route })
  } else {
    const last = location.pathname.split('/').filter(Boolean).at(-1)
    if (last) breadcrumb.push({ label: humanise(last) })
  }

  return (
    <TopBar
      leading={<TapBrand size={22} wordmarkClassName="text-base" />}
      breadcrumb={breadcrumb}
      onMenuClick={toggleSidebar}
      actions={
        <button
          type="button"
          onClick={() => navigate(ROUTES.ALARMS)}
          title="Alarms"
          className="relative flex items-center rounded-md p-2 text-text-secondary hover:bg-bg-hover"
        >
          <Bell size={17} />
          {alarmCount > 0 && (
            <span className="font-mono absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-danger px-1 text-[9px] font-bold text-white">
              {alarmCount}
            </span>
          )}
        </button>
      }
    />
  )
}
